import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    UserSummary,
    PageResponse,
    fetchPolarisUsers,
    fetchPolarisCapabilities,
    resendWelcomeEmail,
    generateAccessLink,
    GenerateAccessLinkResult,
    resetAthleteMediaKit,
    resetAthleteOnboardingWizard,
    saveUserInstantChat,
} from "../services/polarisService";
import PolarisConfirmModal from "./PolarisConfirmModal";
import PolarisRowActionsMenu, { type PolarisRowAction } from "./PolarisRowActionsMenu";
import { formatRoleLabel, isSchoolChatRole } from "../utils/roleLabels";
import "../styles/polaris-admin.css";

const PAGE_SIZE = 20;

function formatDate(iso: string | null | undefined): string {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function statusBadgeClass(status: string): string {
    const s = status.toLowerCase();
    if (s === "active") return "pa-badge pa-badge-active";
    if (s === "trialing") return "pa-badge pa-badge-trialing";
    if (s === "canceled" || s === "cancelled") return "pa-badge pa-badge-canceled";
    if (s === "past_due") return "pa-badge pa-badge-past_due";
    if (s === "none") return "pa-badge pa-badge-none";
    return "pa-badge pa-badge-inactive";
}

function getPageNumbers(current: number, total: number): (number | "ellipsis")[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    const pages: (number | "ellipsis")[] = [];
    pages.push(0);
    if (current > 3) pages.push("ellipsis");
    for (let i = Math.max(1, current - 2); i <= Math.min(total - 2, current + 2); i++) {
        pages.push(i);
    }
    if (current < total - 4) pages.push("ellipsis");
    pages.push(total - 1);
    return pages;
}

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalElements: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    itemLabel?: string;
}

function Pagination({ currentPage, totalPages, totalElements, pageSize, onPageChange, itemLabel = "items" }: PaginationProps) {
    if (totalPages <= 1) return null;
    const start = currentPage * pageSize + 1;
    const end = Math.min(currentPage * pageSize + pageSize, totalElements);
    const pages = getPageNumbers(currentPage, totalPages);

    return (
        <div className="pa-pagination">
            <div className="pa-pagination-info">
                Showing <strong>{start}–{end}</strong> of <strong>{totalElements}</strong> {itemLabel}
            </div>
            <div className="pa-pagination-controls">
                <button className="pa-page-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0} aria-label="Previous page">Previous</button>
                {pages.map((p, i) =>
                    p === "ellipsis" ? (
                        <span key={`e-${i}`} className="pa-page-ellipsis">…</span>
                    ) : (
                        <button key={p} className={`pa-page-btn${p === currentPage ? " active" : ""}`} onClick={() => onPageChange(p as number)} aria-label={`Page ${(p as number) + 1}`} aria-current={p === currentPage ? "page" : undefined}>
                            {(p as number) + 1}
                        </button>
                    )
                )}
                <button className="pa-page-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} aria-label="Next page">Next</button>
            </div>
        </div>
    );
}

type UserSortKey = keyof UserSummary;

interface UserFilters {
    name: string;
    email: string;
    role: string;
    companyName: string;
    districtName: string;
    schoolName: string;
    status: string;
}

export default function PolarisUsersPage() {
    const navigate = useNavigate();
    const [data, setData] = useState<PageResponse<UserSummary> | null>(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<UserSortKey>("profileCreatedAt");
    const [sortAsc, setSortAsc] = useState(false);
    const [resendingUserId, setResendingUserId] = useState<number | null>(null);
    const [generatingAccessLinkUserId, setGeneratingAccessLinkUserId] = useState<number | null>(null);
    const [accessLinkResult, setAccessLinkResult] = useState<GenerateAccessLinkResult | null>(null);
    const [copyNotice, setCopyNotice] = useState<string | null>(null);
    const [resettingUserId, setResettingUserId] = useState<number | null>(null);
    const [resettingOnboardingUserId, setResettingOnboardingUserId] = useState<number | null>(null);
    const [savingChatUserId, setSavingChatUserId] = useState<number | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [confirmUser, setConfirmUser] = useState<UserSummary | null>(null);
    const [confirmAction, setConfirmAction] = useState<"resend" | "generate-access-link" | "reset-media-kit" | "reset-onboarding-wizard" | null>(null);
    const [modalError, setModalError] = useState<string | null>(null);
    const [canResetMediaKit, setCanResetMediaKit] = useState(false);
    const [filters, setFilters] = useState<UserFilters>({
        name: "",
        email: "",
        role: "",
        companyName: "",
        districtName: "",
        schoolName: "",
        status: "",
    });
    const [debouncedFilters, setDebouncedFilters] = useState<UserFilters>({
        name: "",
        email: "",
        role: "",
        companyName: "",
        districtName: "",
        schoolName: "",
        status: "",
    });

    useEffect(() => {
        let cancelled = false;
        fetchPolarisCapabilities()
            .then((caps) => {
                if (!cancelled) setCanResetMediaKit(caps.mediaKitResetEnabled);
            })
            .catch(() => {
                if (!cancelled) setCanResetMediaKit(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            setDebouncedFilters({
                name: filters.name.trim(),
                email: filters.email.trim(),
                role: filters.role.trim(),
                companyName: filters.companyName.trim(),
                districtName: filters.districtName.trim(),
                schoolName: filters.schoolName.trim(),
                status: filters.status.trim(),
            });
        }, 300);
        return () => window.clearTimeout(handle);
    }, [filters]);

    const load = useCallback(async (p: number, userFilters: UserFilters, isInitial: boolean) => {
        if (isInitial) setLoading(true); else setPaging(true);
        setError(null);
        try {
            const result = await fetchPolarisUsers(p, PAGE_SIZE, {
                name: userFilters.name || undefined,
                email: userFilters.email || undefined,
                role: userFilters.role || undefined,
                companyName: userFilters.companyName || undefined,
                districtName: userFilters.districtName || undefined,
                schoolName: userFilters.schoolName || undefined,
                status: userFilters.status || undefined,
            });
            setData(result);
        } catch (e: any) {
            setError(e.message ?? "Failed to load users");
        } finally {
            setLoading(false);
            setPaging(false);
        }
    }, []);

    const isInitialLoad = useRef(true);
    const prevFiltersRef = useRef(debouncedFilters);
    useEffect(() => {
        const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(debouncedFilters);
        prevFiltersRef.current = debouncedFilters;
        if (filtersChanged && page !== 0) {
            setPage(0);
            return;
        }
        const initial = isInitialLoad.current;
        isInitialLoad.current = false;
        load(page, debouncedFilters, initial);
    }, [page, debouncedFilters, load]);

    const handleSort = (key: UserSortKey) => {
        if (sortKey === key) setSortAsc((a) => !a);
        else { setSortKey(key); setSortAsc(true); }
    };

    const requestResendWelcomeEmail = (user: UserSummary) => {
        setModalError(null);
        setConfirmAction("resend");
        setConfirmUser(user);
    };

    const requestGenerateAccessLink = (user: UserSummary) => {
        setModalError(null);
        setConfirmAction("generate-access-link");
        setConfirmUser(user);
    };

    const requestResetMediaKit = (user: UserSummary) => {
        setModalError(null);
        setConfirmAction("reset-media-kit");
        setConfirmUser(user);
    };

    const requestResetOnboardingWizard = (user: UserSummary) => {
        setModalError(null);
        setConfirmAction("reset-onboarding-wizard");
        setConfirmUser(user);
    };

    const closeConfirmModal = () => {
        if (resendingUserId !== null || generatingAccessLinkUserId !== null || resettingUserId !== null || resettingOnboardingUserId !== null) return;
        setConfirmUser(null);
        setConfirmAction(null);
        setModalError(null);
    };

    const closeAccessLinkModal = () => {
        setAccessLinkResult(null);
        setCopyNotice(null);
    };

    const confirmResendWelcomeEmail = async () => {
        if (!confirmUser) return;
        const user = confirmUser;
        setResendingUserId(user.userId);
        setModalError(null);
        setError(null);
        setNotice(null);
        try {
            const result = await resendWelcomeEmail(user.userId);
            setNotice(result.message ? `${result.message} (${user.email})` : `Welcome email resent to ${user.email}.`);
            setConfirmUser(null);
            setConfirmAction(null);
        } catch (e: unknown) {
            setModalError(e instanceof Error ? e.message : "Failed to resend welcome email");
        } finally {
            setResendingUserId(null);
        }
    };

    const confirmGenerateAccessLink = async () => {
        if (!confirmUser) return;
        const user = confirmUser;
        setGeneratingAccessLinkUserId(user.userId);
        setModalError(null);
        setError(null);
        setNotice(null);
        try {
            const result = await generateAccessLink(user.userId);
            setAccessLinkResult(result);
            setConfirmUser(null);
            setConfirmAction(null);
        } catch (e: unknown) {
            setModalError(e instanceof Error ? e.message : "Failed to generate access link");
        } finally {
            setGeneratingAccessLinkUserId(null);
        }
    };

    const copyAccessLink = async () => {
        if (!accessLinkResult?.accessLink) return;
        try {
            await navigator.clipboard.writeText(accessLinkResult.accessLink);
            setCopyNotice("Link copied to clipboard.");
        } catch {
            setCopyNotice("Could not copy automatically — select and copy the link manually.");
        }
    };

    const confirmResetMediaKit = async () => {
        if (!confirmUser) return;
        const user = confirmUser;
        setResettingUserId(user.userId);
        setModalError(null);
        setError(null);
        setNotice(null);
        try {
            const result = await resetAthleteMediaKit(user.userId);
            setNotice(result.message ? `${result.message} (${user.email})` : `Media kit reset for ${user.email}.`);
            setConfirmUser(null);
            setConfirmAction(null);
        } catch (e: unknown) {
            setModalError(e instanceof Error ? e.message : "Failed to reset media kit");
        } finally {
            setResettingUserId(null);
        }
    };

    const confirmResetOnboardingWizard = async () => {
        if (!confirmUser) return;
        const user = confirmUser;
        setResettingOnboardingUserId(user.userId);
        setModalError(null);
        setError(null);
        setNotice(null);
        try {
            const result = await resetAthleteOnboardingWizard(user.userId);
            setNotice(result.message ? `${result.message} (${user.email})` : `Onboarding wizard reset for ${user.email}.`);
            setConfirmUser(null);
            setConfirmAction(null);
        } catch (e: unknown) {
            setModalError(e instanceof Error ? e.message : "Failed to reset onboarding wizard");
        } finally {
            setResettingOnboardingUserId(null);
        }
    };

    const isAthleteRole = (role: string | null | undefined) =>
        (role ?? "").toUpperCase() === "ATHLETE";

    const toggleInstantChat = async (user: UserSummary, enabled: boolean) => {
        setSavingChatUserId(user.userId);
        setError(null);
        try {
            const saved = await saveUserInstantChat(user.userId, enabled);
            setData((current) =>
                current
                    ? {
                          ...current,
                          content: current.content.map((row) =>
                              row.userId === saved.userId ? { ...row, ...saved } : row,
                          ),
                      }
                    : current,
            );
            const name = [saved.firstName, saved.lastName].filter(Boolean).join(" ") || saved.email;
            setNotice(enabled ? `Instant Chat is on for ${name}.` : `Instant Chat is off for ${name}.`);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not update Instant Chat for this user.");
        } finally {
            setSavingChatUserId(null);
        }
    };

    const buildUserActions = (user: UserSummary): PolarisRowAction[] => {
        const actions: PolarisRowAction[] = [
            {
                id: "view-ai-usage",
                label: "View AI usage",
                description: "Tokens, cost, and conversations",
                onSelect: () => navigate(`/polaris-admin/users/${user.userId}/ai-usage`),
                title: `View AI usage for ${user.email}`,
                testId: `view-ai-usage-${user.userId}`,
            },
            {
                id: "view-college-searches",
                label: "College searches",
                description: "Wizard searches and the colleges returned",
                onSelect: () => navigate(`/polaris-admin/users/${user.userId}/college-searches`),
                title: `College searches for ${user.email}`,
                testId: `view-college-searches-${user.userId}`,
            },
            {
                id: "instant-chat",
                label: "Instant Chat",
                description: "Turn Instant Chat on or off for this person",
                onSelect: () =>
                    navigate(
                        `/polaris-admin/instant-chat?tab=users&email=${encodeURIComponent(user.email)}`,
                    ),
                title: `Instant Chat for ${user.email}`,
                testId: `view-user-instant-chat-${user.userId}`,
            },
            {
                id: "generate-access-link",
                label: generatingAccessLinkUserId === user.userId ? "Generating…" : "Generate access link",
                description: "Single-use link, expires in 24 hours",
                onSelect: () => requestGenerateAccessLink(user),
                disabled: generatingAccessLinkUserId === user.userId,
                title: `Generate access link for ${user.email}`,
            },
            {
                id: "resend-welcome-email",
                label: resendingUserId === user.userId ? "Sending…" : "Resend welcome email",
                description: "Emails a new password to the user",
                onSelect: () => requestResendWelcomeEmail(user),
                disabled: resendingUserId === user.userId,
                title: `Resend welcome email to ${user.email}`,
            },
        ];

        if (canResetMediaKit && isAthleteRole(user.role)) {
            actions.push({
                id: "reset-media-kit",
                label: resettingUserId === user.userId ? "Resetting…" : "Reset media kit",
                description: "Clears kit content and share link",
                onSelect: () => requestResetMediaKit(user),
                disabled: resettingUserId === user.userId,
                title: `Reset media kit for ${user.email}`,
                testId: `reset-media-kit-${user.userId}`,
                variant: "destructive",
            });
        }

        if (isAthleteRole(user.role)) {
            actions.push({
                id: "reset-onboarding-wizard",
                label:
                    resettingOnboardingUserId === user.userId
                        ? "Resetting…"
                        : "Reset onboarding wizard",
                description: "Shows welcome wizard again on Home",
                onSelect: () => requestResetOnboardingWizard(user),
                disabled: resettingOnboardingUserId === user.userId,
                title: `Reset onboarding wizard for ${user.email}`,
                testId: `reset-onboarding-wizard-${user.userId}`,
                variant: "destructive",
            });
        }

        return actions;
    };

    const sorted = useMemo(() => {
        const rows = data?.content ?? [];
        return [...rows].sort((a, b) => {
            let av: string;
            let bv: string;
            if (sortKey === "firstName" || sortKey === "lastName") {
                av = `${a.firstName ?? ""} ${a.lastName ?? ""}`;
                bv = `${b.firstName ?? ""} ${b.lastName ?? ""}`;
            } else {
                av = String(a[sortKey] ?? "");
                bv = String(b[sortKey] ?? "");
            }
            const cmp = av.localeCompare(bv, undefined, { numeric: true });
            return sortAsc ? cmp : -cmp;
        });
    }, [data, sortKey, sortAsc]);

    const SortTh = ({ col, label }: { col: UserSortKey; label: string }) => (
        <th className="sortable" onClick={() => handleSort(col)}>
            {label}<span className="pa-sort-indicator">{sortKey === col ? (sortAsc ? " ▲" : " ▼") : " ⇅"}</span>
        </th>
    );

    return (
        <section className="pa-section">
            <h2 className="pa-section-title">Users</h2>
            {error && <div className="pa-error">{error}</div>}
            {notice && <div className="pa-success" role="status">{notice}</div>}
            {loading && <div className="pa-loading">Loading users…</div>}
            {!loading && (
                <div className="pa-table-wrapper" style={{ opacity: paging ? 0.5 : 1, transition: "opacity 0.15s ease" }}>
                    <table className="pa-table">
                        <thead>
                            <tr>
                                <SortTh col="firstName" label="Name" />
                                <SortTh col="email" label="Email" />
                                <SortTh col="role" label="Role" />
                                <SortTh col="companyName" label="Company" />
                                <SortTh col="districtName" label="District Name" />
                                <SortTh col="schoolName" label="School Name" />
                                <SortTh col="profileCreatedAt" label="Created" />
                                <SortTh col="lastLoginTime" label="Last login" />
                                <SortTh col="status" label="Status" />
                                <th>Instant Chat</th>
                                <th>Actions</th>
                            </tr>
                            <tr className="pa-filter-row">
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.name} onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))} /></th>
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.email} onChange={(e) => setFilters((f) => ({ ...f, email: e.target.value }))} /></th>
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.role} onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))} /></th>
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.companyName} onChange={(e) => setFilters((f) => ({ ...f, companyName: e.target.value }))} /></th>
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.districtName} onChange={(e) => setFilters((f) => ({ ...f, districtName: e.target.value }))} /></th>
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.schoolName} onChange={(e) => setFilters((f) => ({ ...f, schoolName: e.target.value }))} /></th>
                                <th />
                                <th />
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} /></th>
                                <th />
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 ? (
                                <tr><td colSpan={11} style={{ textAlign: "center", padding: "24px", color: "var(--muted)" }}>No users found</td></tr>
                            ) : (
                                sorted.map((u) => (
                                    <tr key={u.userId}>
                                        <td>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</td>
                                        <td>{u.email}</td>
                                        <td>{formatRoleLabel(u.role)}</td>
                                        <td>{u.companyName ?? "—"}</td>
                                        <td>{u.districtName ?? "—"}</td>
                                        <td>{u.schoolName ?? "—"}</td>
                                        <td>{formatDate(u.profileCreatedAt)}</td>
                                        <td>{formatDate(u.lastLoginTime)}</td>
                                        <td><span className={statusBadgeClass(u.status ?? "inactive")}>{u.status ?? "—"}</span></td>
                                        <td>
                                            <label className="pa-assist-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={u.instantChatEnabled !== false}
                                                    disabled={savingChatUserId === u.userId || !isSchoolChatRole(u.role)}
                                                    onChange={(e) => void toggleInstantChat(u, e.target.checked)}
                                                    aria-label={`Instant Chat for ${u.email}`}
                                                    data-testid={`instant-chat-user-${u.userId}`}
                                                />
                                                <span>{u.instantChatEnabled !== false ? "On" : "Off"}</span>
                                            </label>
                                        </td>
                                        <td>
                                            <PolarisRowActionsMenu
                                                actions={buildUserActions(u)}
                                                ariaLabel={`Actions for ${u.email}`}
                                                data-testid={`user-actions-${u.userId}`}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    {data && (
                        <Pagination
                            currentPage={page}
                            totalPages={data.totalPages}
                            totalElements={data.totalElements}
                            pageSize={PAGE_SIZE}
                            onPageChange={setPage}
                            itemLabel="users"
                        />
                    )}
                </div>
            )}
            <PolarisConfirmModal
                open={confirmUser !== null && confirmAction === "resend"}
                title="Resend welcome email"
                message={
                    <>
                        Resend the welcome email to <strong>{confirmUser?.email}</strong>?
                        <br />
                        This generates a <strong>new password</strong> and emails it to the user.
                        Their current password will stop working.
                    </>
                }
                confirmLabel="Resend welcome email"
                loadingLabel="Sending…"
                loading={resendingUserId !== null}
                error={modalError}
                onConfirm={confirmResendWelcomeEmail}
                onClose={closeConfirmModal}
            />
            <PolarisConfirmModal
                open={confirmUser !== null && confirmAction === "generate-access-link"}
                title="Generate access link"
                message={
                    <>
                        Generate a single-use access link for <strong>{confirmUser?.email}</strong>?
                        <br />
                        The link expires in <strong>24 hours</strong> and can only be used once.
                        Share it manually via chat or a personal email — the system will <strong>not</strong> send an email.
                        The user&apos;s current password is unchanged.
                    </>
                }
                confirmLabel="Generate access link"
                loadingLabel="Generating…"
                loading={generatingAccessLinkUserId !== null}
                error={modalError}
                onConfirm={confirmGenerateAccessLink}
                onClose={closeConfirmModal}
            />
            {accessLinkResult && (
                <div
                    className="pa-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="pa-access-link-title"
                    onClick={closeAccessLinkModal}
                >
                    <div className="pa-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="pa-modal-close"
                            aria-label="Close"
                            onClick={closeAccessLinkModal}
                        >
                            ×
                        </button>
                        <h3 id="pa-access-link-title" className="pa-modal-title">Access link ready</h3>
                        <div className="pa-modal-message">
                            <p>
                                Share this link with <strong>{accessLinkResult.email}</strong>.
                                It expires {formatDate(accessLinkResult.expiresAt)}.
                            </p>
                            <p><strong>Access link</strong></p>
                            <input
                                id="pa-access-link-field"
                                className="pa-filter-input"
                                readOnly
                                value={accessLinkResult.accessLink}
                                onFocus={(e) => e.target.select()}
                            />
                            {copyNotice && <p className="pa-success" role="status">{copyNotice}</p>}
                        </div>
                        <div className="pa-modal-actions">
                            <button type="button" className="pa-btn pa-btn-secondary" onClick={closeAccessLinkModal}>
                                Close
                            </button>
                            <button
                                type="button"
                                className="pa-btn pa-btn-primary"
                                onClick={() => void copyAccessLink()}
                            >
                                Copy access link
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <PolarisConfirmModal
                open={confirmUser !== null && confirmAction === "reset-media-kit"}
                title="Reset media kit"
                message={
                    <>
                        Reset the media kit for <strong>{confirmUser?.email}</strong>?
                        <br />
                        This clears kit content (about, photos, interests, intro reel, highlights) and
                        invalidates the public share link. Socials and graduation year are kept.
                        The athlete can rebuild from the wizard.
                    </>
                }
                confirmLabel="Reset media kit"
                loadingLabel="Resetting…"
                loading={resettingUserId !== null}
                error={modalError}
                onConfirm={confirmResetMediaKit}
                onClose={closeConfirmModal}
            />
            <PolarisConfirmModal
                open={confirmUser !== null && confirmAction === "reset-onboarding-wizard"}
                title="Reset onboarding wizard"
                message={
                    <>
                        Reset the onboarding welcome wizard for <strong>{confirmUser?.email}</strong>?
                        <br />
                        The athlete will see the welcome wizard overlay again the next time they visit Home,
                        as long as they have not completed all onboarding milestones.
                    </>
                }
                confirmLabel="Reset onboarding wizard"
                loadingLabel="Resetting…"
                loading={resettingOnboardingUserId !== null}
                error={modalError}
                onConfirm={confirmResetOnboardingWizard}
                onClose={closeConfirmModal}
            />
        </section>
    );
}
